/**
 * Менеджер для синхронизации процесса обновления токена авторизации.
 *
 * Реализует паттерн "Single Flight" - гарантирует, что одновременно может
 * выполняться только один запрос на обновление токена. Все последующие
 * запросы будут ожидать результата первого запроса, что предотвращает
 * race condition при множественных одновременных 401 ошибках.
 *
 * @example
 * ```typescript
 * const result = await refreshTokenManager.executeRefresh(async () => {
 *   return await performRefreshRequest();
 * });
 * ```
 */

interface RefreshTokenResponse {
  refreshToken: string;
  refreshKey: string;
  authToken: string;
}

interface RefreshError {
  code?: string;
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
  };
}

class RefreshTokenManager {
  private static readonly MAX_RETRY_ATTEMPTS = 2;
  private static readonly REFRESH_TIMEOUT = 15000;

  private refreshPromise: Promise<RefreshTokenResponse | undefined> | null =
    null;

  /**
   * Выполняет обновление токена с гарантией единственного одновременного выполнения.
   *
   * Если обновление уже выполняется, возвращает промис текущего обновления.
   * Если обновление не выполняется, запускает новое обновление.
   *
   * @param refreshFunction - Функция, выполняющая фактическое обновление токена
   * @returns Promise с результатом обновления токена или undefined в случае ошибки
   */
  async executeRefresh(
    refreshFunction: () => Promise<RefreshTokenResponse | undefined>
  ): Promise<RefreshTokenResponse | undefined> {
    if (this.refreshPromise !== null) {
      // eslint-disable-next-line no-console
      console.log('[RefreshTokenManager] Ожидание завершения текущего refresh');
      return await this.refreshPromise;
    }

    // eslint-disable-next-line no-console
    console.log('[RefreshTokenManager] Начало нового refresh');
    this.refreshPromise = this.performRefreshWithRetry(refreshFunction);

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Выполняет обновление токена с повторными попытками при временных ошибках
   */
  private async performRefreshWithRetry(
    refreshFunction: () => Promise<RefreshTokenResponse | undefined>
  ): Promise<RefreshTokenResponse | undefined> {
    let lastError: unknown;

    for (
      let attempt = 0;
      attempt <= RefreshTokenManager.MAX_RETRY_ATTEMPTS;
      attempt++
    ) {
      try {
        const result = await this.performRefresh(refreshFunction);
        return result;
      } catch (error) {
        lastError = error;
        const isLastAttempt =
          attempt === RefreshTokenManager.MAX_RETRY_ATTEMPTS;
        const shouldRetry = this.shouldRetryRefreshError(error);

        if (isLastAttempt || !shouldRetry) {
          // eslint-disable-next-line no-console
          console.error(
            `[RefreshTokenManager] Финальная ошибка после ${attempt + 1} попыток:`,
            error
          );
          throw error;
        }

        const delayMs = Math.pow(2, attempt) * 1000;
        // eslint-disable-next-line no-console
        console.log(
          `[RefreshTokenManager] Попытка ${attempt + 1}/${RefreshTokenManager.MAX_RETRY_ATTEMPTS + 1} провалилась, retry через ${delayMs}ms`,
          error
        );

        await this.delay(delayMs);
      }
    }

    throw lastError;
  }

  /**
   * Проверяет, является ли ошибка временной и можно ли повторить запрос
   */
  private shouldRetryRefreshError(error: unknown): boolean {
    if (error == null || typeof error !== 'object') {
      return false;
    }

    const refreshError = error as RefreshError;

    if (refreshError.response === undefined || refreshError.response === null) {
      const isNetworkError =
        refreshError.code === 'ECONNRESET' ||
        refreshError.code === 'ETIMEDOUT' ||
        refreshError.code === 'ENOTFOUND' ||
        refreshError.code === 'ERR_NETWORK' ||
        refreshError.message?.includes('Network Error') === true;

      return isNetworkError;
    }

    const status = refreshError.response.status;

    if (status === undefined) {
      return false;
    }

    const isTemporaryServerError =
      status === 408 ||
      status === 429 ||
      status === 502 ||
      status === 503 ||
      status === 504;

    return isTemporaryServerError;
  }

  /**
   * Задержка выполнения
   */
  private async delay(ms: number): Promise<void> {
    return await new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Внутренний метод для выполнения обновления токена с таймаутом
   */
  private async performRefresh(
    refreshFunction: () => Promise<RefreshTokenResponse | undefined>
  ): Promise<RefreshTokenResponse | undefined> {
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      const result = await Promise.race([
        refreshFunction(),
        new Promise<undefined>((_, reject) => {
          timeoutId = setTimeout(() => {
            reject(
              new Error(
                `Refresh token timeout после ${RefreshTokenManager.REFRESH_TIMEOUT}ms`
              )
            );
          }, RefreshTokenManager.REFRESH_TIMEOUT);
        })
      ]);

      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }

      if (result !== undefined) {
        // eslint-disable-next-line no-console
        console.log('[RefreshTokenManager] Refresh успешно завершен');
      } else {
        // eslint-disable-next-line no-console
        console.log('[RefreshTokenManager] Refresh вернул undefined');
      }

      return result;
    } catch (error) {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
      throw error;
    }
  }

  async reset(): Promise<void> {
    if (this.refreshPromise !== null) {
      // eslint-disable-next-line no-console
      console.log(
        '[RefreshTokenManager] Ожидание завершения refresh перед сбросом'
      );
      await this.refreshPromise.catch(() => {});
    }
    // eslint-disable-next-line no-console
    console.log('[RefreshTokenManager] Сброс состояния');
    this.refreshPromise = null;
  }

  get isCurrentlyRefreshing(): boolean {
    return this.refreshPromise !== null;
  }
}

export const refreshTokenManager = new RefreshTokenManager();
